import { Platform } from '@angular/cdk/platform';
import {
  Component,
  ElementRef,
  HostBinding,
  HostListener,
  Input,
  OnInit,
  Optional,
  Self,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NgControl,
  UntypedFormControl,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import { PlIcon } from '@app/icon-library/icons';
import { IconFrame } from '@app/icon-library/lib/pl-icon.component';

let identifier: number = 0;

enum CustomValidatorErrors {
  maxlength = 'maxlength',
  minNumberWords = 'minNumberWords',
}

export enum TextAreaHeight {
  medium = 'medium',
  large = 'large',
}

@Component({
  selector: 'pl-textarea',
  templateUrl: './pl-textarea.component.html',
  styleUrls: ['./pl-textarea.component.scss'],
})
export class PlTextareaComponent implements OnInit, ControlValueAccessor {
  @Input({ required: true }) public label: string = '';
  @Input() public height: TextAreaHeight = TextAreaHeight.medium;
  @Input() public isDisabled: boolean = false;
  @Input() public isFormSubmitted: boolean = false;
  @Input() public maxlength: number | null = 10000;
  @Input() public minNumberWords: number | null = 0;
  @Input() public placeholder: string = '';
  @Input() public showBorderValidationColor: boolean = true;
  @Input() public showRightIcon: boolean = true;

  @HostBinding('class.outline-none') public tailwindClasses: boolean = true;

  public value: string = '';

  public identifier: string = `form-pl-textarea-${identifier++}`;

  private onChangeCallback!: (value: string) => void;
  private onTouchedCallback!: () => void;

  public readonly CustomValidatorErrors: typeof CustomValidatorErrors = CustomValidatorErrors;
  public readonly IconFrame: typeof IconFrame = IconFrame;
  public readonly PlIcon: typeof PlIcon = PlIcon;
  public readonly TextAreaHeight: typeof TextAreaHeight = TextAreaHeight;

  @HostBinding('tabindex') public tabindex: number = 1;
  @HostListener('focus') public onFocus(): void {
    this.inputElement.nativeElement.focus({ preventScroll: true });
  }

  @ViewChild('inputElement', { read: ElementRef }) public inputElement!: ElementRef;

  public isFocused: boolean = false;

  constructor(
    @Optional() @Self() public controlDir: NgControl,
    public platform: Platform,
  ) {
    if (this.controlDir) {
      this.controlDir.valueAccessor = this;
    }
  }

  public ngOnInit(): void {
    if (this.controlDir) {
      const control: AbstractControl | null = this.controlDir.control;

      if (control) {
        const validators: ValidatorFn[] = control.validator ? [control.validator] : [];
        control.setValidators([this.validate.bind(this, validators)]);
        control.updateValueAndValidity({ emitEvent: false });
      }
    }
  }

  private validate(validators: ValidatorFn[]): ValidationErrors | null {
    const allErrors: any = {};
    for (const validator of validators) {
      const formControl: UntypedFormControl = new UntypedFormControl(this.value);
      const hasError: ValidationErrors | null = validator(formControl);
      if (hasError) {
        Object.entries(hasError).forEach(([errorName, isOnError]: [string, boolean]) => {
          allErrors[errorName] = isOnError;
        });
      }
    }

    if (this.minNumberWords && this.minNumberWords > 0) {
      const numberOfWords: number = this.value.split(/\s|\.|,|;/).length;
      if (numberOfWords < this.minNumberWords) {
        allErrors[CustomValidatorErrors.minNumberWords] = true;
      }
    }

    if (this.maxlength !== null && this.value?.length > this.maxlength) {
      allErrors[CustomValidatorErrors.maxlength] = {
        requiredLength: this.maxlength,
        actualLength: this.value.length,
      };
    }

    if (Object.keys(allErrors).length > 0) {
      return allErrors;
    }

    return null;
  }

  public registerOnChange(fn: (value: string) => void): void {
    this.onChangeCallback = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouchedCallback = fn;
  }

  public writeValue(valueToWrite: string): void {
    if (valueToWrite !== undefined) {
      this.value = valueToWrite;
    }
    this.updateValueAndValidity();
  }

  public onCustomInputFocus(): void {
    console.log('TONIO PlTextareaComponent onCustomInputFocus =');
    this.isFocused = true;
  }

  public onBlur(): void {
    this.isFocused = false;
    this.onTouched();
  }

  public onTouched(): void {
    if (this.onTouchedCallback) {
      this.onTouchedCallback();
    }
  }

  public onChange(): void {
    const openDivTag: RegExp = new RegExp('<div>', 'g');
    const closeDivTag: RegExp = new RegExp('</div>', 'g');
    const brTag: RegExp = new RegExp('<br>', 'g');

    let outputValue: string;
    if (this.platform.FIREFOX) {
      outputValue = this.inputElement.nativeElement.innerHTML
        .replace(openDivTag, '')
        .replace(closeDivTag, '')
        .replace(brTag, '\n');
    } else {
      outputValue = this.inputElement.nativeElement.innerHTML
        .replace(openDivTag, '\n')
        .replace(closeDivTag, '')
        .replace(brTag, '');
    }

    this.controlDir.control?.updateValueAndValidity({ emitEvent: false });
    this.emitChanges(outputValue);
  }

  private emitChanges(valueToEmit: string): void {
    this.onChangeCallback(valueToEmit);
  }

  protected updateValueAndValidity(): void {
    if (this.controlDir.control) {
      this.controlDir.control.updateValueAndValidity({
        onlySelf: false,
        emitEvent: false,
      });
    }
  }
}
